/**
 * Ergebnisse der Volltextsuche statt der Gliederung: je Abschnitt Nummer, Titel und Ausschnitte,
 * Fundstellen hervorgehoben. Ein Klick öffnet den Abschnitt; dort sind alle Vorkommen markiert.
 */
import { useTranslation } from "react-i18next";

import { cn } from "../lib/utils";
import type { SearchResult } from "./search";

const Marked = ({ text, marks }: { text: string; marks: [number, number][] }) => {
  const parts: React.ReactNode[] = [];
  let at = 0;
  marks.forEach(([a, b], i) => {
    if (a > at) parts.push(text.slice(at, a));
    parts.push(
      <mark key={i} className="rounded-sm bg-yellow-300/70 px-0.5 text-foreground dark:bg-yellow-500/40">
        {text.slice(a, b)}
      </mark>,
    );
    at = b;
  });
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
};

export function SearchResults(props: { results: SearchResult[]; selectedId: string | null; onSelect: (sectionId: string) => void }) {
  const { t } = useTranslation();
  const hits = props.results.reduce((n, r) => n + r.hits, 0);
  if (!props.results.length) return <p className="px-3 py-2 text-sm text-muted-foreground">{t("guidelinesPage.search.none")}</p>;
  return (
    <div className="flex flex-col" data-testid="search-results">
      <div className="px-3 py-1 text-xs text-muted-foreground">{t("guidelinesPage.search.summary", { hits, sections: props.results.length })}</div>
      {props.results.map((r) => (
        <button
          key={r.sectionId}
          type="button"
          onClick={() => props.onSelect(r.sectionId)}
          className={cn("border-b px-3 py-2 text-left text-sm hover:bg-accent", r.sectionId === props.selectedId && "bg-accent")}
          data-search-result={r.number}
        >
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 font-medium tabular-nums">{r.number}</span>
            <span className="min-w-0 truncate font-medium">
              <Marked text={r.title} marks={r.titleMarks} />
            </span>
            {r.draftOnly ? <span className="ml-auto shrink-0 rounded bg-amber-100 px-1 text-[10px] text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t("guidelinesPage.draft")}</span> : null}
          </div>
          {r.snippets.map((s, i) => (
            <div key={i} className="mt-1 line-clamp-3 text-xs text-muted-foreground">
              <Marked text={s.text} marks={s.marks} />
            </div>
          ))}
        </button>
      ))}
    </div>
  );
}
